import { FormikErrors, useFormikContext } from "formik";
import { TFunction } from "i18next";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { EventFormValues, EventUsersFormValues } from "../../types/event";
import { EventFormCard } from "./EventFormCard";
import { getPrevLink, useSubmitEventStep } from "./eventStepperUtils";
import { EventUsersStepFormFields } from "./EventUsersStepFormFields";

export function getEventUsersStepValidator(t: TFunction) {
	return (values: EventUsersFormValues): FormikErrors<EventUsersFormValues> => {
		const errors: FormikErrors<EventFormValues> = {};
		values.eventType?.userResponsibilityRequirements.forEach(({ userResponsibility, requirement }) => {
			if (requirement === "required" && (values.eventUsers?.[userResponsibility.id]?.length ?? 0) === 0) {
				errors.eventUsers = { ...(errors.eventUsers ?? {}), [userResponsibility.id]: t("Field is required") };
			}
		});

		return errors;
	};
}

export const isEventUsersStepValid = (errors: FormikErrors<EventUsersFormValues>) => {
	return !errors.eventUsers;
};

interface Props {
	variant?: "elevated" | "outlined";
}

export const EventUsersStep: FC<Props> = ({ variant }) => {
	const { t } = useTranslation();
	const { values, errors, isValidating, setTouched, touched } = useFormikContext<EventUsersFormValues>();
	const submitEventStep = useSubmitEventStep();

	const handleNextClick = useCallback(async () => {
		await setTouched({
			...touched,
			eventUsers: Object.fromEntries(
				(values.eventType?.userResponsibilityRequirements ?? []).map(({ userResponsibility }) => [
					userResponsibility.id,
					true,
				]),
			) as any,
		});

		if (!isValidating && isEventUsersStepValid(errors)) {
			await submitEventStep(values.eventType, "users");
		}
	}, [errors, isValidating, setTouched, submitEventStep, touched, values.eventType]);

	return (
		<EventFormCard
			variant={variant}
			title={t("Users")}
			prevLink={getPrevLink(values.eventType, "users")}
			onNextClick={handleNextClick}
			showLanguageSwitcher={false}
		>
			<EventUsersStepFormFields />
		</EventFormCard>
	);
};
